import React, {useEffect, useState} from 'react';
import {Box, Button, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {Add} from '@icons/index';
import {ConfirmActionProps, OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {ManageReport, Report, reportQuery, reportsService, ReportStatusEnum, searchReportsEffect} from '@store/reports';
import {finalize, Observable} from 'rxjs';
import {useSnackbar} from 'notistack';
import {GlobalStatus} from '@store/common/globalStatus.enum';
import TabsComponent from '@components/tabs/Tabs.component';
import NewTableComponent from '@components/table/NewTable.component';
import ConfirmModal from '@components/modal/Confirm.modal';
import {emitOnce} from '@ngneat/elf';
import TableReportFiltersComponent from '@screens/admin/reports/table/filters/TableReportFilters.component';
import SearchComponent from '@components/inputs/search/Search.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {Outlet, useNavigate} from 'react-router-dom';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
//searchReportEffect

const ReportsScreen = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const searchReports = useEffectFn(searchReportsEffect);

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const [{reports, loading}] = useObservable(reportQuery.reports$);
  const [filterStatus] = useObservable(reportQuery.status$);

  const [filters] = useObservable(reportQuery.filters$);
  const [sortModel] = useObservable(reportQuery.sort$);
  const [tablePagination] = useObservable(reportQuery.pagination$);


  const downloadReport = (report: Report) => {
    reportsService.downloadReport(ManageReport.toDto(report)).subscribe({
      next: () => {
        enqueueSnackbar(t('reports.message.download.success'), {variant: 'success'});
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };


  useEffect(() => {
    searchReports({status: filterStatus, filters, tablePagination, sort: sortModel});
  }, [filters, filterStatus, searchReports, sortModel, tablePagination]);

  const handleConfirmValidation = (type: OtherActionType, report: Report) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;

    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = reportsService.deleteReport(report.id);
        break;
      case OtherActionType.ARCHIVE:
        validationObservable = reportsService.activeOrArchiveReport(report.id, ReportStatusEnum.ARCHIVED);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${type.toLowerCase()}.success.report`), {variant: 'success'});
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} width="100%" pt="34px" px="24px" spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography fontSize="30px" fontWeight="700">
          {t('reports.title')}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant="contained" color="primary"
            startIcon={<Add/>}
            onClick={() => navigate('generate', {replace: true})}>
            {t('reports.addButton')}
          </Button>
        </Stack>

      </Stack>
      <TabsComponent
        ariaLabel="secondary"
        handleChange={(value) => {
          reportsService.setStatus(GlobalStatus.tabs[value]);
        }}
        value={GlobalStatus.tabs.indexOf(filterStatus ?? undefined)}
        tabs={[t('routes.all'), t('routes.archived')]}/>
      <Stack flex={1} spacing={2} pb="25px">
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
          <Box width="500px">
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  reportsService.setFilters('search')(value);
                  reportsService.setPagination('page')(1);
                });
              }}/>
          </Box>
          <TableReportFiltersComponent
            filters={filters.tableFilters}
            handleSave={reportsService.setFilters('tableFilters')}
            handleReset={() => reportsService.resetFilters()}/>
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT({
              field: 'organisation.name',
              label: `${t('reports.table.organisation')}`,
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, Report>) => params.row.organisation.name,
              width: 250,
            }),
            COLUMN_TYPES.TEXT({
              field: 'organisationUnits',
              label: `${t('reports.table.units')}`,
              sortable: false,
              valueFormatter: (params) => {
                if (params?.value) {
                  if (params.value.length === 1)
                    return params.value[0].name;
                  if (params.value.length > 1)
                    return t('global.multiple', {count: params.value.length});
                }
                return '';
              },
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'report.startDate',
              label: `${t('reports.table.period')}`,
              color: 'secondary',
              sortable: true,
              fullWidth: true,
              valueGetter: (params: GridValueGetterParams<any, Report>) => params.row,
              valueFormatter: (params) => {
                if (params?.value) {
                  return `${t('global.date', {date: new Date(params.value.startDate)})} - ${t('global.date', {date: new Date(params.value.endDate)})}`;
                }
                return;

              },
              minWidth: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'report.createdAt',
              label: `${t('reports.table.generatedAt')}`,
              color: 'secondary',
              sortable: true,
              fullWidth: true,
              valueGetter: (params: GridValueGetterParams<any, Report>) => params.row,
              valueFormatter: (params) => {
                if (params?.value) {
                  return `${t('global.date', {date: new Date(params.value.createdAt)})}`;
                }
                return;

              },
              minWidth: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'author',
              label: `${t('reports.table.author')}`,
              color: 'secondary',
              fullWidth: true,
              valueFormatter: (params) => {
                if (params?.value) {
                  return params.value;
                }
                return;

              },
              minWidth: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'wasteFamilies',
              label: `${t('reports.table.parameters')}`,
              color: 'secondary',
              fullWidth: true,
              valueFormatter: (params) => {
                if (params?.value) {
                  return params?.value.map((w) => w.name).join(' / ');
                }
                return '';
              },
              minWidth: 350,
            }),
            COLUMN_TYPES.ACTIONS({
              minWidth: 70,
              handleDownload: (report: any) => {
                downloadReport(report.row);
              },
              actions: [
                {
                  type: OtherActionType.DELETE,
                  handleClick: (report: Report) => setOpenConfirmAction({
                    type: OtherActionType.DELETE,
                    row: report
                  }),
                },
              ],
            }),
          ]}
          disableRowSelection
          handlePageSizeChange={reportsService.setPagination('limit')}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => reportsService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={reportsService.setSort}
          sortModel={[sortModel]}
          //rowsSelected={selectedReports}
          totalRows={reportQuery.totalRows}
          data={reports}/>
        {!!openConfirmAction && (
          <ConfirmModal
            handleClose={() => setOpenConfirmAction(false)}
            description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.report`)}
            handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
            title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
            validateButton={OtherActionType.label(openConfirmAction.type)}/>
        )}
        <Outlet/>
      </Stack>
    </Stack>
  );
};

export default ReportsScreen;
